import React, { useEffect, useState } from 'react';
import {
  ButtonSetWrapper,
  ConfirmPasswordField,
  NewPasswordField,
  ResetCodeField,
  ResetPasswordCodeFormInputGrid,
  StyledText,
} from '../components/shared/StyledComponents';
import { Button, hasValue, useFormInputHandler } from 'swipeclock-frontend';
import {
  FORM_IDS,
  formDefaultValues,
  useLoginContext,
} from 'providers/LoginProvider';
import PoweredByLabel from '../components/shared/PoweredByLabel';
import { usePostSetPasswordMutation } from 'services/login';
import { useFormValidation } from '../loginValidation';
import WhiteLabelLogo from '../components/shared/WhiteLabelLogo';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useHistory } from 'react-router-dom';
import AnimatedAlert from '../components/shared/AnimatedAlert';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';

export default function ResetPasswordCode() {
  const [resetSuccess, setResetSuccess] = useState(false);
  const [setPassword] = usePostSetPasswordMutation();
  const { errorContext, validationCallbackCodeReset } = useFormValidation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlLogin = searchParams.get('login');
  const urlEmail = searchParams.get('email');
  const urlResetCode = searchParams.get('resetCode');
  const locationState = location.state || {};

  const loginState = hasValue(urlLogin) ? urlLogin : locationState.login;
  const emailState = hasValue(urlEmail) ? urlEmail : locationState.email;

  const {
    actions,
    meta: { pages },
  } = useLoginContext();
  const { state: whiteLabelState } = useWhiteLabelContext();

  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid,
    setDirty,
    validationRef,
  } = useFormInputHandler({
    errorContext,
    ids: [FORM_IDS.RESETCODE, FORM_IDS.NEWPASSWORD, FORM_IDS.CONFIRMPASSWORD],
    validationCallback: validationCallbackCodeReset,
  });

  const handleBackToLogin = () => {
    actions.setCurrentPage(pages.START);
    actions.setGlobalMessage();
    history.replace('/clock/web/login');
  };

  const handleGoBack = () => {
    const state = { username: loginState, email: emailState };
    actions.setCurrentPage(pages.FORGOTPASS);
    actions.setGlobalMessage();
    history.replace({ pathname: '/clock/web/login/password/forgot', state });
  };

  const handleResetPassword = async () => {
    try {
      if (!isValid(pages.RESETCODE)) {
        throw new Error('Form is invalid!');
      }
      const body = {
        Id: loginState,
        Email: emailState,
        Code: validationRef.current[FORM_IDS.RESETCODE]?.value,
        Password: validationRef.current[FORM_IDS.NEWPASSWORD]?.value,
      };
      const response = await setPassword({ body }).unwrap();
      console.log('RESET PASSWORD SUCCESS: ', response);
      setResetSuccess(true);
      const messageText = 'Success! Your password has been changed.';
      actions.setGlobalMessage({ message: messageText, severity: 'success' });
    } catch (error) {
      console.error(error);
      const errorText =
        error?.data?.Message ||
        'There was an error resetting your password. Please try again.';
      actions.setGlobalMessage({ message: errorText, severity: 'error' });
    }
  };

  const handleKeyDownReset = (event) => {
    if (event.key === 'Enter') {
      handleResetPassword();
      setDirty((prevDirty) => ({
        ...prevDirty,
        [FORM_IDS.CONFIRMPASSWORD]: true,
      }));
    }
  };

  useEffect(() => {
    if (!hasValue(loginState) || !hasValue(emailState)) {
      const errorText =
        'There was an error when setting your login and/or email in this form. Please go back to the prior page and try again.';
      actions.setGlobalMessage({ message: errorText, severity: 'error' });
    }
  }, []);

  return (
    <ResetPasswordCodeFormInputGrid
      isMobile={isMobile}
      noHeader={whiteLabelState?.noHeader}
    >
      <WhiteLabelLogo
        whiteLabelState={whiteLabelState}
        headerText="Reset Password"
      />
      <AnimatedAlert />
      <StyledText>
        Enter the reset code we emailed to{' '}
        <strong>{emailState || 'you'}</strong>, then select a new password for{' '}
        <strong>{loginState || 'your account'}</strong>.
      </StyledText>
      <ResetCodeField
        autoComplete="resetCode"
        error={getError(FORM_IDS.RESETCODE)}
        onBlur={handleBlur(FORM_IDS.RESETCODE)}
        onChange={handleChange(FORM_IDS.RESETCODE)}
        validationRef={validationRef}
        id={FORM_IDS.RESETCODE}
        defaultValue={
          hasValue(urlResetCode) ? urlResetCode : formDefaultValues.resetCode
        }
        label="Reset Code"
        placeholder="Enter Reset Code Here"
        required
        size="small"
      />
      <NewPasswordField
        autoComplete="newPassword"
        error={getError(FORM_IDS.NEWPASSWORD)}
        id={FORM_IDS.NEWPASSWORD}
        label="New Password"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.NEWPASSWORD)}
        onChange={handleChange(FORM_IDS.NEWPASSWORD)}
        defaultValue={formDefaultValues.newPassword}
        placeholder="Enter a New Password"
        required
        validationRef={validationRef}
        size="small"
      />
      <ConfirmPasswordField
        autoComplete="confirmPassword"
        error={getError(FORM_IDS.CONFIRMPASSWORD)}
        id={FORM_IDS.CONFIRMPASSWORD}
        label="Confirm Password"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.CONFIRMPASSWORD)}
        onChange={handleChange(FORM_IDS.CONFIRMPASSWORD)}
        defaultValue={formDefaultValues.confirmPassword}
        placeholder="Re-enter Your New Password"
        required
        validationRef={validationRef}
        inputProps={{ onKeyDown: handleKeyDownReset }}
        size="small"
      />
      <ButtonSetWrapper isMobile={isMobile}>
        {resetSuccess && (
          <Button
            id="sc-user_choice_back_to_login_button"
            size="large"
            onClick={handleBackToLogin}
          >
            Back to Login
          </Button>
        )}
        {!resetSuccess && (
          <>
            <Button
              id="sc-go_back_button"
              size="large"
              variant="outlined"
              onClick={handleGoBack}
            >
              Go Back
            </Button>
            <Button
              id="sc-user_choice_reset_button"
              size="large"
              onClick={handleResetPassword}
              disabled={disabled || !isValid(pages.RESETPASSCODE)}
            >
              Reset
            </Button>
          </>
        )}
      </ButtonSetWrapper>
      <PoweredByLabel whiteLabelState={whiteLabelState} />
    </ResetPasswordCodeFormInputGrid>
  );
}
